import React from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, ClockIcon } from 'lucide-react';
import { OrderButton } from './OrderButton';
import { AmbientLight } from './AmbientLight';
import { CircleLight } from './CircleLight';
import { useLanguage } from '../context/LanguageContext';
export function PreOrder() {
  const {
    t
  } = useLanguage();
  const p = t.preorder;
  return <section id="preorder" className="relative overflow-hidden py-32 lg:py-48 bg-texture-dark text-cream" data-id="element-270" data-mp="2uxq1m0n6k4ww">
      <AmbientLight variant="split" seam data-id="element-271" data-mp="2zx2oumhyre00" />
      <div className="relative z-10 max-w-[1300px] mx-auto px-8 lg:px-14 grid lg:grid-cols-12 gap-14 lg:gap-20 items-center" data-id="element-272" data-mp="2zw1bgbdh6ff4">
        {/* Narrative */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 1
      }} className="lg:col-span-6" data-id="element-273" data-mp="2q682li4pweek">
          <div className="flex items-center gap-3 text-caramel text-[10px] tracking-[0.4em] uppercase mb-10" data-id="element-274" data-mp="3epl1n3ye4za2">
            <span className="w-10 h-px bg-caramel/40" data-id="element-275" data-mp="3te7g73g71n08" />
            {p.eyebrow}
          </div>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-cream leading-[0.95] tracking-[-0.02em] font-light" data-id="element-276" data-mp="3oo0q0z619ufv">
            {p.title1}
            <br data-id="element-277" data-mp="686o8su9vsrz" />
            <span className="italic font-extralight" data-id="element-278" data-mp="6buglonze954">{p.title2}</span>
          </h2>
          <p className="mt-10 text-cream/60 leading-[1.85] text-[15px] max-w-md" data-id="element-279" data-mp="1aewpzxji8ob">
            {p.copy}
          </p>

          <div className="mt-12 flex flex-col items-start gap-7" data-id="element-280" data-mp="g5ury89uwama">
            <OrderButton data-id="element-281" data-mp="ba5r81m3onoe" />
            <a href="#waitlist" className="group inline-flex items-center gap-6 text-cream" data-id="element-282" data-mp="b6rnoq1pa8fd">
              <span className="w-10 h-px bg-cream/40 group-hover:bg-caramel group-hover:w-16 transition-all duration-500" data-id="element-283" data-mp="2d0qzdciqirsl" />
              <span className="font-serif italic text-[15px] tracking-[0.05em]" data-id="element-284" data-mp="32peixsts1qou">
                {p.cta}
              </span>
              <span className="w-10 h-px bg-cream/40 group-hover:bg-caramel group-hover:w-16 transition-all duration-500" data-id="element-285" data-mp="36q88srqs5fkd" />
            </a>
          </div>
        </motion.div>

        {/* Pickup details card */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 1,
        delay: 0.15
      }} className="lg:col-span-6" data-id="element-286" data-mp="3gm1hu15aiqxe">
          <div className="relative" data-id="element-287" data-mp="3bc509yow5xf">
            <div className="absolute -inset-2 border border-cream/10" aria-hidden data-id="element-288" data-mp="88uhubftv5ap" />
            <div className="relative bg-texture-espresso border border-cream/10 p-10 lg:p-12" data-id="element-289" data-mp="d874ow6p8cda">
              {/* Location */}
              <div className="flex items-start gap-5" data-id="element-290" data-mp="1xqrbi5prnxhi">
                <span className="relative mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-caramel/40 text-caramel" aria-hidden data-id="element-291" data-mp="1sq3nlswu3wri">
                  <CircleLight speed="slow" data-id="element-292" data-mp="1ith4b8k5pdrl" />
                  <MapPinIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-293" data-mp="2gfy0ubl2fnca" />
                </span>
                <div data-id="element-294" data-mp="2ghs9orj2bp00">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-cream/40 mb-2" data-id="element-295" data-mp="2bh4j26yqxa6b">
                    {p.marketLabel}
                  </p>
                  <p className="font-serif text-2xl lg:text-[26px] text-cream font-light leading-tight" data-id="element-296" data-mp="26ojrlc3bb95y">
                    {p.marketName}
                  </p>
                  <address className="mt-3 not-italic text-cream/60 leading-[1.7] text-[14px]" data-id="element-297" data-mp="2z6p5izu3ypkc">
                    {p.address1}
                    <br data-id="element-298" data-mp="2ue5ua9iw86ip" />
                    {p.address2}
                  </address>
                </div>
              </div>

              <div className="my-9 h-px bg-cream/10" aria-hidden data-id="element-299" data-mp="2f0237278d478" />

              {/* Hours */}
              <div className="flex items-start gap-5" data-id="element-300" data-mp="2juplgjbn52dd">
                <span className="relative mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-caramel/40 text-caramel" aria-hidden data-id="element-301" data-mp="250kyamzsyucb">
                  <CircleLight speed="slow" data-id="element-302" data-mp="2xop5jcy0vfh4" />
                  <ClockIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-303" data-mp="32n0hbt7vo01z" />
                </span>
                <div data-id="element-304" data-mp="2nydruyojdrx1">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-cream/40 mb-2" data-id="element-305" data-mp="2nyxayipxamcn">
                    {p.hoursLabel}
                  </p>
                  <p className="font-serif text-2xl lg:text-[26px] text-cream font-light leading-tight" data-id="element-306" data-mp="2svnrf75n3jyl">
                    {p.hoursDay}
                  </p>
                  <p className="mt-3 text-cream/60 text-[14px] tracking-[0.04em]" data-id="element-307" data-mp="3le1goicwtxm2">
                    {p.hoursTime}
                  </p>
                </div>
              </div>

              <p className="mt-10 pt-8 border-t border-cream/10 text-[11px] tracking-[0.15em] uppercase text-cream/40 leading-[1.7]" data-id="element-308" data-mp="14zzfe0bzlsnu">
                {p.note}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}